// Who may see the assessments saved on this device, and whether they are
// unlocked right now.
//
//   Online and signed in   -> the server's device key unlocks them silently.
//     Offline access is switched per user by an admin (off by default):
//       off -> no PIN prompt; any PIN on this device is removed, and local
//              copies are kept only until they have synced.
//       on  -> if this device has no PIN, the user is asked to set one, or
//              can skip ("I won't work offline on this device"). A skip is
//              remembered for this user on this device; they can opt in later
//              from the Assessments page. After a lockout the user must
//              choose a new PIN (or skip) before the portal opens.
//   Signed in, but offline -> status 'locked': the PIN unlocks them.
//   Not signed in          -> /offline offers the PIN unlock to users who
//                             signed in here within 7 days. Unlocked this way,
//                             only the assessment pages are available.
//   Sign-in expires mid-form -> nothing is locked: the data key is already in
//                             memory, so the therapist keeps working while sync
//                             waits for them to sign in again.
//
// The device reports PIN set / skipped / locked out / removed to the server
// so managers can see who has offline access set up where.
//
// Signing out locks the vault (the data key leaves memory). Saved data stays
// on the device until it is synced and no longer needed.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { assessmentApi } from '../services/assessmentApi';
import {
  VaultError, clearPendingDeviceEvent, declineOffline, getDeviceId, isUnlocked, lockVault, offlineProfiles,
  removePin, requestPersistence, setPin as storeSetPin, unlockWithPin as storeUnlockWithPin,
  unlockWithServerKey, vaultStatus,
} from '../services/assessmentStore';
import { refreshOfflineData } from '../services/assessmentSync';

const VAULT_ROLES = ['therapist', 'manager', 'admin'];

const DeviceVaultContext = createContext(null);

function deviceLabel() {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ? 'iPhone/iPad'
    : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac'
      : /Linux/.test(ua) ? 'Linux' : 'device';
  return `${browser} on ${os}`;
}

// Never blocks the therapist. PIN changes are queued in the vault first
// (assessmentStore) and cleared only once the server has them.
async function reportDevice(event, at) {
  try {
    const id = await getDeviceId();
    await assessmentApi.deviceEvent(id, { event, at, label: deviceLabel() });
    return true;
  } catch {
    return false;
  }
}

async function flushDeviceEvent(userId) {
  const status = await vaultStatus(userId).catch(() => null);
  const pending = status?.pendingDeviceEvent;
  if (pending && await reportDevice(pending.event, pending.at)) {
    await clearPendingDeviceEvent(userId, pending);
  }
}

export function DeviceVaultProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [state, setState] = useState({
    status: 'idle', userId: null, profile: null, error: null, reason: null,
    offlineEnabled: false, hasPin: false,
  });
  const activeUser = useRef(null);

  const signedIn = !!user && isAuthenticated();
  const applies = signedIn && VAULT_ROLES.includes(user.role);

  const refresh = useCallback((userId, offlineEnabled) => {
    refreshOfflineData(userId, { offlineEnabled })
      .catch((err) => console.error('Offline data refresh failed', err));
  }, []);

  // Signed in: unlock through the server key, or fall back to the PIN if the
  // server cannot be reached.
  useEffect(() => {
    if (!applies) return undefined;
    let cancelled = false;
    const userId = String(user.userId);
    const profile = { firstName: user.firstName, lastName: user.lastName, role: user.role, email: user.email };
    activeUser.current = { userId, profile };

    (async () => {
      setState((s) => (s.userId === userId && s.status === 'unlocked' ? s : { ...s, status: 'checking', userId, profile }));
      requestPersistence();
      try {
        const { key, offlineEnabled } = await assessmentApi.deviceKey();
        let status = await unlockWithServerKey(userId, key, profile, !!offlineEnabled);
        if (cancelled) return;

        // Anything not yet reported (e.g. a lockout that happened offline).
        await flushDeviceEvent(userId);

        const base = { userId, profile, error: null, offline: false, offlineEnabled: !!offlineEnabled };
        if (!offlineEnabled) {
          if (status.hasPin) {
            await removePin(userId);
            flushDeviceEvent(userId);
          }
          setState({ ...base, status: 'unlocked', reason: null, hasPin: false });
        } else if (status.hasPin) {
          reportDevice('seen');
          setState({ ...base, status: 'unlocked', reason: null, hasPin: true });
        } else if (status.declined && !status.lockedOut) {
          setState({ ...base, status: 'unlocked', reason: null, hasPin: false });
        } else {
          status = await vaultStatus(userId);
          setState({ ...base, status: 'needs-pin', reason: status.lockedOut ? 'locked-out' : 'first-time', hasPin: false });
        }
        refresh(userId, !!offlineEnabled);
      } catch (err) {
        if (cancelled) return;
        if (isUnlocked(userId)) {
          setState((s) => ({ ...s, status: 'unlocked', userId, profile, error: null, reason: null, offline: false }));
        } else if (err instanceof VaultError) {
          setState((s) => ({ ...s, status: 'error', userId, profile, error: err.message, reason: err.code }));
        } else if (err.status === 0) {
          const v = await vaultStatus(userId).catch(() => null);
          const usable = v?.hasPin && v.offlineEnabled;
          setState((s) => ({ ...s, status: usable ? 'locked' : 'unavailable', userId, profile, error: null,
            reason: v?.lockedOut ? 'locked-out' : null }));
        } else {
          setState((s) => ({ ...s, status: 'error', userId, profile, error: err.message, reason: null }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [applies, user?.userId]); // eslint-disable-line

  // Not signed in (token expired, signed out, or opened offline).
  useEffect(() => {
    if (signedIn) return;
    const last = activeUser.current;
    if (last && isUnlocked(last.userId)) {
      setState((s) => ({ ...s, status: 'unlocked', userId: last.userId, profile: last.profile, error: null,
        reason: null, offline: true }));
    } else {
      setState((s) => (s.status === 'unlocked' && s.offline ? s
        : { status: 'signed-out', userId: null, profile: null, error: null, reason: null, offlineEnabled: false, hasPin: false }));
    }
  }, [signedIn]);

  // Back online with a valid sign-in: push edits and refresh offline data.
  useEffect(() => {
    if (state.status !== 'unlocked' || state.offline) return undefined;
    const onOnline = () => refresh(state.userId, state.offlineEnabled);
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [state.status, state.offline, state.userId, state.offlineEnabled, refresh]);

  const setPin = useCallback(async (pin) => {
    await storeSetPin(state.userId, pin);
    flushDeviceEvent(state.userId);
    setState((s) => ({ ...s, status: 'unlocked', reason: null, hasPin: true, offline: !signedIn }));
    refresh(state.userId, true);
  }, [state.userId, signedIn, refresh]);

  const skipOffline = useCallback(async () => {
    await declineOffline(state.userId);
    flushDeviceEvent(state.userId);
    setState((s) => ({ ...s, status: 'unlocked', reason: null, hasPin: false }));
  }, [state.userId]);

  // Opt in later from the Assessments page.
  const enableOffline = useCallback(() => {
    setState((s) => (s.offlineEnabled && !s.hasPin ? { ...s, status: 'needs-pin', reason: 'opt-in' } : s));
  }, []);

  const unlockWithPin = useCallback(async (userId, pin, profile) => {
    await storeUnlockWithPin(userId, pin);
    activeUser.current = { userId: String(userId), profile };
    setState({ status: 'unlocked', userId: String(userId), profile, error: null, reason: null,
      offline: !signedIn, offlineEnabled: true, hasPin: true });
  }, [signedIn]);

  const lock = useCallback(() => {
    lockVault();
    activeUser.current = null;
    setState({ status: signedIn ? 'locked' : 'signed-out', userId: null, profile: null, error: null,
      reason: null, offlineEnabled: false, hasPin: false });
  }, [signedIn]);

  const value = {
    ...state,
    applies,
    // The identity to show and authorise against on assessment pages: the
    // signed-in user, or the user who unlocked offline.
    identity: signedIn ? user : (state.status === 'unlocked' && state.offline
      ? { ...state.profile, userId: state.userId } : null),
    setPin,
    skipOffline,
    enableOffline,
    unlockWithPin,
    lock,
    offlineProfiles,
  };

  return <DeviceVaultContext.Provider value={value}>{children}</DeviceVaultContext.Provider>;
}

export function useDeviceVault() {
  const ctx = useContext(DeviceVaultContext);
  if (!ctx) throw new Error('useDeviceVault must be used inside DeviceVaultProvider');
  return ctx;
}
